import type { AgentComponentToolOption } from '@reach/shared-types/agent-components';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { appRoutes } from '../../../app-routes';
import { Modal } from '../../../components/common/Modal';
import { Button } from '../../../components/ui/button';
import { toolReturnState } from '../../ai-tools/tool-return-path';
import styles from './AddToolDialog.module.css';
import { ToolOptionRow } from './ToolOptionRow';

interface AddToolDialogProps {
  tools: AgentComponentToolOption[];
  onAdd: (toolIds: string[]) => void;
  onClose: () => void;
}

const addButtonLabel = (selectedCount: number): string => {
  const noToolIsSelected = selectedCount === 0;
  const oneToolIsSelected = selectedCount === 1;
  if (noToolIsSelected) {
    return 'Add tools';
  }
  return oneToolIsSelected ? 'Add 1 tool' : `Add ${selectedCount} tools`;
};

export const AddToolDialog = ({ tools, onAdd, onClose }: AddToolDialogProps) => {
  const { pathname } = useLocation();
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const toolsAreAvailable = tools.length > 0;
  const toolsAreSelected = selectedToolIds.length > 0;

  const toggleTool = (toolId: string) =>
    setSelectedToolIds(current =>
      current.includes(toolId) ? current.filter(id => id !== toolId) : [...current, toolId],
    );

  const addSelectedTools = () =>
    onAdd(tools.filter(tool => selectedToolIds.includes(tool.id)).map(tool => tool.id));

  return (
    <Modal
      title="Add tools to this component"
      description="Pick existing tools. Each one stays shared with any other component that uses it."
      onClose={onClose}>
      {toolsAreAvailable ? (
        <ul className={styles.toolOptions}>
          {tools.map(tool => (
            <ToolOptionRow
              key={tool.id}
              tool={tool}
              selected={selectedToolIds.includes(tool.id)}
              onToggle={() => toggleTool(tool.id)}
            />
          ))}
        </ul>
      ) : (
        <p className={styles.empty}>You have no other active tools.</p>
      )}
      <footer className={styles.actions}>
        <Link to={appRoutes.AI_TOOL_NEW} state={toolReturnState(pathname)} className={styles.createTool}>
          Create a new tool
        </Link>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {toolsAreAvailable && (
          <Button type="button" disabled={!toolsAreSelected} onClick={addSelectedTools}>
            {addButtonLabel(selectedToolIds.length)}
          </Button>
        )}
      </footer>
    </Modal>
  );
};
